import React, { useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, X, UserRound, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { validateStaffId } from '@/services/validationService';

/**
 * StaffScanGate — gates an operator-facing station by requiring the user
 * to scan a valid Staff-ID before any action fires. Stores the validated
 * Staff-ID in sessionStorage (`module_staff_id`) where apiClient picks it
 * up and attaches `X-Staff-Id` to every outbound request.
 *
 * Usage:
 *   <StaffScanGate stationName="Flexi JW Tagging">
 *     <YourStationPage />
 *   </StaffScanGate>
 *
 * Behaviour:
 *   • On mount, checks sessionStorage — if a prior valid scan exists,
 *     children render immediately.
 *   • Otherwise shows a full-bleed prompt with scan input + live validation.
 *   • A "Change Staff" control re-opens the prompt mid-session.
 */
export default function StaffScanGate({ stationName = 'Station', children }) {
  const [staffId, setStaffId] = useState(sessionStorage.getItem('module_staff_id') || '');
  const [staffName, setStaffName] = useState(sessionStorage.getItem('module_staff_name') || '');
  const [input, setInput] = useState('');
  const [validating, setValidating] = useState(false);
  const [showPrompt, setShowPrompt] = useState(!sessionStorage.getItem('module_staff_id'));

  useEffect(() => {
    // If someone else cleared sessionStorage (e.g. logout), re-prompt.
    const onStorage = () => {
      const s = sessionStorage.getItem('module_staff_id');
      setStaffId(s || '');
      setStaffName(sessionStorage.getItem('module_staff_name') || '');
      setShowPrompt(!s);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const onSubmit = async () => {
    const v = input.trim();
    if (!v) return;
    setValidating(true);
    try {
      const res = await validateStaffId(v);
      if (!res.valid) {
        toast.error(res.message || 'Invalid Staff-ID');
        setInput('');
        return;
      }
      sessionStorage.setItem('module_staff_id', res.staffId);
      sessionStorage.setItem('module_staff_name', res.staffName || '');
      setStaffId(res.staffId);
      setStaffName(res.staffName || '');
      setInput('');
      setShowPrompt(false);
      toast.success(`Welcome ${res.staffName}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Staff validation failed');
    } finally {
      setValidating(false);
    }
  };

  const changeStaff = () => {
    sessionStorage.removeItem('module_staff_id');
    sessionStorage.removeItem('module_staff_name');
    setStaffId('');
    setStaffName('');
    setShowPrompt(true);
  };

  if (showPrompt) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center p-4"
           data-testid="staff-scan-gate">
        <Card className="w-full max-w-md border-2 border-[#2D5F4F]/20 shadow-xl">
          <CardContent className="p-8 space-y-5 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#2D5F4F]/10 flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 text-[#2D5F4F]" />
            </div>
            <div>
              <h2 className="text-xl font-bold font-display text-[#1A1F2E]">
                Scan Your Staff ID
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {stationName} requires floor accountability
              </p>
            </div>
            <div className="text-left space-y-2">
              <Label htmlFor="staff-scan-input">Staff ID</Label>
              <Input
                id="staff-scan-input"
                autoFocus
                value={input}
                onChange={(e) => setInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
                placeholder="Scan or type e.g. V0104…"
                disabled={validating}
                data-testid="staff-scan-input"
                className="font-mono"
              />
            </div>
            <Button
              onClick={onSubmit}
              disabled={validating || !input.trim()}
              className="w-full bg-[#2D5F4F] hover:bg-[#234A3D]"
              data-testid="staff-scan-submit"
            >
              {validating ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Validating…</>
              ) : (
                <>Enter Station</>
              )}
            </Button>
            <p className="text-xs text-gray-400">
              Every action you take here will be logged against your Staff ID.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="staff-gate-passed">
      <div className="bg-[#2D5F4F] text-white px-4 py-2 flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <UserRound className="w-4 h-4" />
          <span>
            On shift: <strong>{staffName}</strong>
            <Badge variant="outline"
                   className="ml-2 bg-white/10 text-white border-white/30 font-mono text-[10px]">
              {staffId}
            </Badge>
          </span>
        </div>
        <button
          onClick={changeStaff}
          className="text-xs underline hover:text-white/80"
          data-testid="staff-gate-change-btn"
        >
          Change staff
        </button>
      </div>
      {children}
    </div>
  );
}
